import React, { useMemo, useState } from 'react';
import { Card } from '../../components/UI';
import {
  DEFAULT_GEO_PROVIDER_ORDER,
  getUserPreferences,
  resetUserPreferences,
  saveUserPreferences,
  type UserPreferences,
} from '../../services/userPreferences';

export const Settings: React.FC = () => {
  const initialPreferences = useMemo(() => getUserPreferences(), []);
  const [preferences, setPreferences] = useState<UserPreferences>(initialPreferences);
  const [durationOptionsInput, setDurationOptionsInput] = useState(
    initialPreferences.appointmentDurationOptions.join(', '),
  );
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const updateField = <K extends keyof UserPreferences>(field: K, value: UserPreferences[K]) => {
    setPreferences((previous) => ({
      ...previous,
      [field]: value,
    }));
    setSaveMessage(null);
  };

  const handleSave = () => {
    const parsedAppointmentDurations = durationOptionsInput
      .split(/[\s,;]+/)
      .map((part) => Number.parseInt(part, 10))
      .filter((value) => Number.isFinite(value));

    const saved = saveUserPreferences({
      ...preferences,
      appointmentDurationOptions: parsedAppointmentDurations,
    });
    setPreferences(saved);
    setDurationOptionsInput(saved.appointmentDurationOptions.join(', '));
    setSaveMessage('Paramètres enregistrés.');
  };

  const handleReset = () => {
    const reset = resetUserPreferences();
    setPreferences(reset);
    setDurationOptionsInput(reset.appointmentDurationOptions.join(', '));
    setSaveMessage('Paramètres réinitialisés.');
  };

  const parseOptionalNumber = (value: string): number | null => {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }

    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : null;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Paramètres utilisateur</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configurez votre expérience (cartographie, géocodage, organisation).
        </p>
      </div>

      <Card title="Cartographie" subtitle="Préférences d'affichage des cartes">
        <div className="space-y-4">
          <div>
            <label htmlFor="mapProvider" className="mb-1 block text-sm font-medium text-gray-700">
              Provider carte préféré
            </label>
            <select
              id="mapProvider"
              value={preferences.mapProvider}
              onChange={(event) => updateField('mapProvider', event.target.value as UserPreferences['mapProvider'])}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="google">Google Maps</option>
              <option value="osm">OpenStreetMap</option>
            </select>
          </div>

          <label className="flex items-center gap-3 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={preferences.enableMapFallback}
              onChange={(event) => updateField('enableMapFallback', event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>Activer le fallback automatique vers OpenStreetMap en cas de problème provider</span>
          </label>
        </div>
      </Card>

      <Card title="Organisation" subtitle="Préférences d'agenda et de localisation">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="defaultVisitDuration" className="mb-1 block text-sm font-medium text-gray-700">
              Durée de visite par défaut (minutes)
            </label>
            <input
              id="defaultVisitDuration"
              type="number"
              min={15}
              max={240}
              step={5}
              value={preferences.defaultVisitDuration}
              onChange={(event) => updateField('defaultVisitDuration', Number(event.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="appointmentDurationOptions" className="mb-1 block text-sm font-medium text-gray-700">
              Durées disponibles des rendez-vous (minutes)
            </label>
            <input
              id="appointmentDurationOptions"
              type="text"
              value={durationOptionsInput}
              onChange={(event) => setDurationOptionsInput(event.target.value)}
              placeholder="30, 45, 60, 90"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Sépare les valeurs par des virgules. Exemple: 20, 30, 45, 60, 75, 90.
            </p>
          </div>

          <div>
            <label htmlFor="slotStepMinutes" className="mb-1 block text-sm font-medium text-gray-700">
              Granularité des créneaux suggérés
            </label>
            <select
              id="slotStepMinutes"
              value={preferences.slotStepMinutes}
              onChange={(event) => updateField('slotStepMinutes', Number(event.target.value))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value={5}>Toutes les 5 minutes</option>
              <option value={10}>Toutes les 10 minutes</option>
              <option value={15}>Toutes les 15 minutes</option>
              <option value={30}>Toutes les 30 minutes</option>
              <option value={60}>Toutes les heures</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Intervalle entre deux créneaux candidats dans l'algorithme de suggestion. 15 min offre un bon équilibre précision/performance.
            </p>
          </div>

          <div>
            <label htmlFor="timezone" className="mb-1 block text-sm font-medium text-gray-700">
              Fuseau horaire
            </label>
            <input
              id="timezone"
              type="text"
              value={preferences.timezone}
              onChange={(event) => updateField('timezone', event.target.value)}
              placeholder="Europe/Paris"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2 rounded-lg border border-gray-200 p-4">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={preferences.agendaDenseMode}
                onChange={(event) => updateField('agendaDenseMode', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Activer le mode dense dans l'agenda (rendu compact quand les cases sont chargées)</span>
            </label>
          </div>

          <div>
            <label htmlFor="workdayStartTime" className="mb-1 block text-sm font-medium text-gray-700">
              Heure de début de journée
            </label>
            <input
              id="workdayStartTime"
              type="time"
              value={preferences.workdayStartTime}
              onChange={(event) => updateField('workdayStartTime', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="workdayEndTime" className="mb-1 block text-sm font-medium text-gray-700">
              Heure de fin de journée
            </label>
            <input
              id="workdayEndTime"
              type="time"
              value={preferences.workdayEndTime}
              onChange={(event) => updateField('workdayEndTime', event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2 rounded-lg border border-gray-200 p-4">
            <label className="mb-3 flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={preferences.enableMiddayBreak}
                onChange={(event) => updateField('enableMiddayBreak', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Activer une pause dans la journée</span>
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="breakStartTime" className="mb-1 block text-sm font-medium text-gray-700">
                  Début de pause
                </label>
                <input
                  id="breakStartTime"
                  type="time"
                  value={preferences.breakStartTime}
                  onChange={(event) => updateField('breakStartTime', event.target.value)}
                  disabled={!preferences.enableMiddayBreak}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label htmlFor="breakEndTime" className="mb-1 block text-sm font-medium text-gray-700">
                  Fin de pause
                </label>
                <input
                  id="breakEndTime"
                  type="time"
                  value={preferences.breakEndTime}
                  onChange={(event) => updateField('breakEndTime', event.target.value)}
                  disabled={!preferences.enableMiddayBreak}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-100"
                />
              </div>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Ces horaires sont utilisés pour proposer des créneaux optimisés compatibles avec ton rythme de travail.
            </p>

            <div className="mt-3">
              <label className="flex items-center gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={preferences.returnToBaseForMiddayBreak}
                  onChange={(event) => updateField('returnToBaseForMiddayBreak', event.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Retour à la base pendant la pause déjeuner (si activé)</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Si activé, l'optimisation intercalera la base professionnelle autour de la pause midi quand c'est pertinent.
              </p>
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="professionalBaseAddress" className="mb-1 block text-sm font-medium text-gray-700">
              Base professionnelle (libellé)
            </label>
            <input
              id="professionalBaseAddress"
              type="text"
              value={preferences.professionalBaseAddress}
              onChange={(event) => updateField('professionalBaseAddress', event.target.value)}
              placeholder="Ex: Cabinet principal, 12 rue de la Paix, Paris"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Utilisé comme point de départ/retour pour calculer précisément les trajets sur les créneaux isolés.
            </p>
          </div>

          <div>
            <label htmlFor="professionalBaseLatitude" className="mb-1 block text-sm font-medium text-gray-700">
              Latitude base pro
            </label>
            <input
              id="professionalBaseLatitude"
              type="number"
              step="0.000001"
              min={-90}
              max={90}
              value={preferences.professionalBaseLatitude ?? ''}
              onChange={(event) => updateField('professionalBaseLatitude', parseOptionalNumber(event.target.value))}
              placeholder="48.856614"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="professionalBaseLongitude" className="mb-1 block text-sm font-medium text-gray-700">
              Longitude base pro
            </label>
            <input
              id="professionalBaseLongitude"
              type="number"
              step="0.000001"
              min={-180}
              max={180}
              value={preferences.professionalBaseLongitude ?? ''}
              onChange={(event) => updateField('professionalBaseLongitude', parseOptionalNumber(event.target.value))}
              placeholder="2.352222"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      <Card title="Géocodage" subtitle="Préparation pour future synchronisation backend">
        <div className="space-y-2">
          <label htmlFor="geoOrder" className="mb-1 block text-sm font-medium text-gray-700">
            Ordre des providers
          </label>
          <input
            id="geoOrder"
            type="text"
            value={preferences.geocodingProviderOrder}
            onChange={(event) => updateField('geocodingProviderOrder', event.target.value)}
            placeholder={DEFAULT_GEO_PROVIDER_ORDER}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500">
            Exemple: geoplateforme,google,openrouteservice,nominatim
          </p>
        </div>
      </Card>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {saveMessage && <p className="text-sm text-emerald-700">{saveMessage}</p>}
        <button
          type="button"
          onClick={handleReset}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Réinitialiser
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Enregistrer
        </button>
      </div>
    </div>
  );
};

export default Settings;
